export type AskVisual =
  | "waterfall"
  | "demand"
  | "trend"
  | "heatmap"
  | "priority"
  | "intervention"
  | "contribution";
export type AskAnswer = {
  question: string;
  conclusion: string;
  quantification: string[];
  confidence: number;
  confidenceReason: string;
  caveat: string;
  evidenceCount: number;
  visual: AskVisual;
  permissionLimited?: boolean;
};

export const productScopeAnswers: Record<
  "Current Account" | "Mobile App" | "Delivery" | "Insurance",
  AskAnswer
> = {
  "Mobile App": {
    question: "Why did contact volume increase this period?",
    conclusion:
      "Android identity-verification failures are the largest source of new assisted demand in the Mobile App scope.",
    quantification: [
      "Identity-verification contacts increased 38%",
      "Abandonment rose from 11.2% to 16.7%",
      "1,100 fewer weekly onboarding completions are associated with the change",
    ],
    confidence: 88,
    confidenceReason:
      "Release timing, device concentration and failure language align across 614 linked records.",
    caveat:
      "The evidence supports a likely association, not confirmed release causality.",
    evidenceCount: 614,
    visual: "trend",
  },
  "Current Account": {
    question: "Why did contact volume increase this period?",
    conclusion:
      "Payment-date confusion after mid-cycle plan changes is the clearest source of repeat Current Account demand.",
    quantification: [
      "5,840 payment-date contacts in the period",
      "31% contacted again within a median of 38 hours",
      "Portal and confirmation dates disagree in 89% of the reviewed sample",
    ],
    confidence: 91,
    confidenceReason:
      "Date pairs and linked service journeys agree across 1,089 eligible records.",
    caveat:
      "Two sampled cases indicate agent miscoding rather than a date mismatch.",
    evidenceCount: 1089,
    visual: "demand",
  },
  Delivery: {
    question: "Why did contact volume increase this period?",
    conclusion:
      "Delivery demand did not increase: revised tracking language reduced order-status contact while the comparison group remained stable.",
    quantification: [
      "Order-status contacts fell 13%, from 5,655 to 4,920",
      "The 95% interval excludes the prior baseline",
      "Failed-delivery complaints stayed within the 4.5% guardrail at 4.1%",
    ],
    confidence: 86,
    confidenceReason:
      "The decline persists across two regions and 1,284 linked evidence records.",
    caveat: "This is a measured association, not a randomised causal estimate.",
    evidenceCount: 1284,
    visual: "intervention",
  },
  Insurance: {
    question: "Why did contact volume increase this period?",
    conclusion:
      "Renewal explanation work increased because customers can see the new total but not what changed within it.",
    quantification: [
      "Average handling time increased from 8.1 to 12.4 minutes",
      "Renewal explanation contacts increased 17%",
      "The price-breakdown test is favourable but remains statistically inconclusive",
    ],
    confidence: 84,
    confidenceReason:
      "Representative language is consistent across 742 records, with campaign mix retained as an alternative.",
    caveat:
      "Continue measurement before treating the letter design as confirmed cause.",
    evidenceCount: 742,
    visual: "contribution",
  },
};

export const askAnswers: AskAnswer[] = [
  {
    question: "Why did contact volume increase this period?",
    conclusion:
      "Assisted contact rose 12.4%; Android verification and direct-debit date confusion are the largest likely contributors.",
    quantification: [
      "6,242 additional interactions versus the previous 30 days",
      "Android verification added 1,842; payment-date confusion added 918",
      "Delivery tracking offset 612 contacts",
    ],
    confidence: 91,
    confidenceReason:
      "Four observed contributors explain 68% of movement across 2,445 evidence records.",
    caveat:
      "32% remains unexplained, and temporal alignment does not confirm cause.",
    evidenceCount: 2445,
    visual: "waterfall",
  },
  {
    question: "Which issues create the most repeat contact?",
    conclusion:
      "Payment-date confusion has the highest material repeat-contact burden.",
    quantification: [
      "31% repeat rate across 5,840 contacts",
      "Median time to repeat is 38 hours",
      "Identity verification follows at 27% across 4,210 contacts",
    ],
    confidence: 89,
    confidenceReason:
      "Journey resolution and source-case relationships agree across 89% of eligible records.",
    caveat: "Identity resolution is incomplete for 3.2% of interactions.",
    evidenceCount: 1089,
    visual: "demand",
  },
  {
    question: "What changed after the Android release?",
    conclusion:
      "Identity-verification failure language moved outside its expected range 42 minutes after Android 8.14 adoption crossed 25%.",
    quantification: [
      "Contacts increased 38%",
      "Abandonment rose from 11.2% to 16.7%",
      "The pattern is concentrated on Android 14; iOS stayed in range",
    ],
    confidence: 88,
    confidenceReason:
      "Release timing, device concentration and failure language align.",
    caveat:
      "This is an association; campaign mix and platform permission changes remain alternatives.",
    evidenceCount: 614,
    visual: "trend",
  },
  {
    question:
      "Which customer segments are struggling with identity verification?",
    conclusion:
      "Android 14 new customers and digital-only customers show the largest verification friction.",
    quantification: [
      "Android 14 volume is 42% above expected share",
      "New-customer effort is 44% above its cohort baseline",
      "Assisted customers are 21% above expected repeat contact",
    ],
    confidence: 87,
    confidenceReason:
      "Cohort fields are complete for 91% of verification interactions.",
    caveat:
      "184 Vulnerable Customer records are permission-limited and excluded from representative evidence.",
    evidenceCount: 496,
    visual: "heatmap",
    permissionLimited: true,
  },
  {
    question: "What should the product team prioritise?",
    conclusion:
      "Prioritise the Android document-capture regression, then the direct-debit date mismatch.",
    quantification: [
      "Android combines 49% abandonment impact with 38% acceleration",
      "Direct debit contributes 918 additional contacts and 31% repeat rate",
      "Delivery tracking is improving and should remain in measurement",
    ],
    confidence: 90,
    confidenceReason:
      "Priority combines impact, acceleration, volume and evidence strength.",
    caveat:
      "Commercial impact is estimated and should not replace product-risk judgement.",
    evidenceCount: 1703,
    visual: "priority",
  },
  {
    question: "Did the delivery-tracking change work?",
    conclusion:
      "The change is effective: order-status contacts fell 13% relative to a stable comparison series.",
    quantification: [
      "Contacts fell from 5,655 to 4,920",
      "The 95% interval excludes the baseline after stabilisation",
      "Failed-delivery complaints stayed within the 4.5% guardrail at 4.1%",
    ],
    confidence: 86,
    confidenceReason:
      "The decline follows phased rollout in two regions and persists through the observation window.",
    caveat: "This is a measured association, not a randomised causal estimate.",
    evidenceCount: 1284,
    visual: "intervention",
  },
  {
    question:
      "What evidence contradicts the direct-debit root-cause hypothesis?",
    conclusion:
      "Two sampled cases indicate agent miscoding rather than a portal/email date mismatch.",
    quantification: [
      "2 contradictory records among 1,089 linked interactions",
      "89% of sampled date pairs support the mismatch pattern",
      "9% have insufficient source context",
    ],
    confidence: 82,
    confidenceReason:
      "Contradictory sampling is deliberate and source metadata is complete.",
    caveat:
      "The evidence weakens universality, not the overall likely explanation.",
    evidenceCount: 1089,
    visual: "contribution",
  },
];
export const insufficientAnswer: AskAnswer = {
  question: "",
  conclusion:
    "Vox does not have enough governed evidence to answer that question reliably.",
  quantification: [
    "No canonical metric or qualifying evidence set matched the request",
    "Try narrowing the period, product, journey or customer segment",
  ],
  confidence: 0,
  confidenceReason: "No evidence-backed conclusion can be produced.",
  caveat: "Vox will not fabricate a quantified answer.",
  evidenceCount: 0,
  visual: "contribution",
};
